import { useEffect, useState } from "react";
import { api } from "../data";
import { Dialog } from "./ui";
import { t } from "../lib/sprache";
import { fehlerText } from "../lib/fehler";
import { meldeBericht, oeffneMelden, setMeldenOeffner } from "../lib/melden";
import { fehlerAgentur } from "../lib/fehlerbericht";

/**
 * Fenster „Fehler melden“ (0176): Meldung als Zitat, freiwillig „Was wolltest du gerade machen?“, das automatisch erstellte
 * Bild der Seite (abwählbar, Eingabefelder geschwärzt) und ein kurzer Datenschutzhinweis. Einmal in der App eingehängt.
 */
export function MeldenHost() {
  const [offen, setOffen] = useState<{ message: string; screenshot: string | null; bezug: Record<string, string> } | null>(null);
  const [notiz, setNotiz] = useState("");
  const [mitBild, setMitBild] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState<string | null>(null);
  useEffect(() => {
    setMeldenOeffner((m) => { setOffen(m); setNotiz(""); setMitBild(!!m.screenshot); setFehler(null); setFertig(null); });
    return () => setMeldenOeffner(null);
  }, []);
  const schliessen = () => setOffen(null);
  const senden = async () => {
    if (!offen) return;
    setBusy(true);
    setFehler(null);
    try {
      const r = await api.read<{ id: string } | string>("report_error", {
        p_report: meldeBericht({ message: offen.message, user_note: notiz.trim() || undefined, screenshot: mitBild ? offen.screenshot : null, bezug: offen.bezug }),
      });
      const id = typeof r === "string" ? (JSON.parse(r) as { id: string }).id : r.id;
      setFertig(id);
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
    }
  };
  // Tickets gibt es nur für das Team einer Agentur (Creator fragen ihre Agentur)
  const ticketMoeglich = location.pathname.startsWith("/agentur") && !!fehlerAgentur();
  return (
    <div data-melden-aus="1">
      <Dialog open={!!offen} onClose={schliessen} title={t("Fehler melden", "Report a problem")}>
        {fertig ? (
          <div className="stack" role="status">
            <p>{t("Danke! Deine Meldung ist bei uns angekommen. Sobald der Fehler behoben ist, sagen wir dir per Mail Bescheid.",
              "Thank you! Your report has reached us. Once the problem is fixed, we will let you know by email.")}</p>
            <div className="row">
              {ticketMoeglich && (
                <a className="btn" href={`/agentur/hilfe/neu?meldung=${fertig}`} onClick={(e) => {
                  // Wechsel innerhalb der App (das Fenster liegt außerhalb des Routers): Verlauf setzen, Router benachrichtigen
                  e.preventDefault();
                  history.pushState({}, "", `/agentur/hilfe/neu?meldung=${fertig}`);
                  dispatchEvent(new PopStateEvent("popstate"));
                  schliessen();
                }}>{t("Als Ticket weiterführen", "Continue as a ticket")}</a>
              )}
              <button type="button" className="primary" onClick={schliessen}>{t("Schließen", "Close")}</button>
            </div>
          </div>
        ) : (
          <form className="stack" onSubmit={(e) => { e.preventDefault(); void senden(); }}>
            <blockquote className="tk-melden-zitat">{offen?.message}</blockquote>
            <label>
              {t("Was wolltest du gerade machen?", "What were you trying to do?")} <small>{t("(freiwillig)", "(optional)")}</small>
              <textarea rows={3} maxLength={2000} value={notiz} onChange={(e) => setNotiz(e.target.value)} />
            </label>
            {offen?.screenshot && (
              <div className="stack tk-melden-bild">
                <label className="row tk-check">
                  <input type="checkbox" checked={mitBild} onChange={(e) => setMitBild(e.target.checked)} />
                  <span>{t("Bild der Seite mitsenden", "Include a picture of the page")}</span>
                </label>
                {mitBild && <img src={offen.screenshot} alt={t("Bild der Seite, Eingabefelder geschwärzt", "Picture of the page, input fields blacked out")} />}
              </div>
            )}
            {offen?.screenshot && <small>{t("Beim Absenden wird ein Bild der aktuellen Seite mitgeschickt, wenn das Häkchen gesetzt ist; die Vorschau oben zeigt es. Eingabefelder werden automatisch geschwärzt.",
              "When sending, a picture of the current page is included if the box is ticked; the preview above shows it. Input fields are blacked out automatically.")}</small>}
            <small>{t("Mitgeschickt werden die Meldung, die Seite, deine letzten Klicks (nur die Beschriftung der Knöpfe), technische Fehler und App-Version, auf Wunsch das Bild der Seite. Nicht mitgeschickt werden Eingaben, Passwörter, Schlüssel und Zahlungsdaten. Wir löschen Meldungen nach 180 Tagen.",
              "We send the message, the page, your last clicks (only button labels), technical errors and the app version, and the picture of the page if you like. Inputs, passwords, keys and payment details are not sent. We delete reports after 180 days.")}</small>
            {fehler && <p className="tk-error" role="alert">{fehler}</p>}
            <div className="row">
              <button type="submit" className="primary" disabled={busy} aria-busy={busy}>{busy ? t("Wird gesendet …", "Sending …") : t("Senden", "Send")}</button>
              <button type="button" disabled={busy} onClick={schliessen}>{t("Abbrechen", "Cancel")}</button>
            </div>
          </form>
        )}
      </Dialog>
    </div>
  );
}

/** Knopf „Melden“ an einer Fehlermeldung; nimmt zuerst das Bild der Seite auf */
export function MeldenKnopf({ message, className = "quiet" }: { message: string; className?: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <button type="button" className={className} disabled={busy} aria-busy={busy} data-melden-aus="1"
      onClick={() => { setBusy(true); void oeffneMelden(message).finally(() => setBusy(false)); }}>
      {busy ? t("Einen Moment …", "One moment …") : t("Melden", "Report")}
    </button>
  );
}
